import OmSpinner from "@/components/OmSpinner";
import { T } from "@/components/T";
import { useEffect, useState } from "react";
import { normalizeSrc } from "@/lib/images";
import { Link } from "react-router-dom";
import { fetchJournal } from "@/lib/api";
import Seo from "@/components/Seo";
import { MandalaMotif } from "@/components/MotifBackground";
import { ArrowRight, Clock } from "lucide-react";

const fmtDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s.slice(0, 10)).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch { return s.slice(0, 10); }
};

const Meta = ({ p, className = "" }) => (
  <p className={`flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-3 ${className}`}>
    {p.author && <span className="uppercase tracking-[0.1em]">{p.author}</span>}
    {p.author && (p.published_at || p.read_time) && <span className="text-line">·</span>}
    {p.published_at && <span>{fmtDate(p.published_at)}</span>}
    {p.read_time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {p.read_time}</span>}
  </p>
);

export default function Journal() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { fetchJournal().then(setPosts).finally(() => setLoading(false)); }, []);

  const [lead, ...rest] = posts;

  return (
    <>
      <Seo title="Patrika" description="The Dharmartha Patrika — reflections, explainers and stories from the knowledge traditions of Bharat." path="/patrika" />
      <section className="relative overflow-hidden border-b border-line bg-[var(--surface)]">
        <MandalaMotif className="pointer-events-none absolute -right-16 -top-8 h-72 w-72 text-[var(--gold)] opacity-[0.07]" />
        <div className="relative z-[2] mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-20">
          <p className="overline-label mb-3">धर्मार्थ पत्रिका</p>
          <h1 className="font-serif-latin text-4xl text-ink md:text-6xl"><T>Dharmartha Patrika</T></h1>
          <div className="mt-5 flex items-center">
            <span className="h-px w-10 bg-[var(--gold-muted)]" />
            <span className="mx-3 h-1.5 w-1.5 rotate-45 bg-[var(--gold)]" />
            <span className="h-px w-24 bg-[var(--gold-muted)]" />
          </div>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink-2"><T>Reflections, explainers and stories from the living knowledge traditions of Bharat.</T></p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14 md:px-8 md:py-16">
        {loading ? <OmSpinner /> : posts.length === 0 ? (
          <div className="border border-line bg-surface p-16 text-center text-ink-3"><T>No articles published yet. Please check back soon.</T></div>
        ) : (
          <>
            {/* Lead / featured article */}
            {lead && (
              <Link
                to={`/patrika/${lead.slug}`}
                data-testid={`journal-card-${lead.slug}`}
                className="group mb-14 grid overflow-hidden border border-line bg-surface transition-all duration-500 hover:border-[var(--gold)] hover:shadow-[0_10px_40px_rgba(44,38,33,0.08)] md:grid-cols-2"
              >
                {lead.cover_image && (
                  <div className="relative h-64 overflow-hidden md:h-full md:min-h-[360px]">
                    <img src={normalizeSrc(lead.cover_image)} alt={lead.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute left-5 top-5 bg-[var(--saffron)] px-3 py-1 text-xs uppercase tracking-[0.12em] text-white"><T>Latest</T></span>
                  </div>
                )}
                <div className="flex flex-col justify-center p-7 md:p-12">
                  {lead.deva_title && <p className="font-deva text-xl text-saffron deva-lh">{lead.deva_title}</p>}
                  <h2 className="mt-2 font-serif-latin text-3xl leading-tight text-ink md:text-4xl">{lead.title}</h2>
                  {lead.excerpt && <p className="mt-4 max-w-prose text-[15px] leading-relaxed text-ink-2 md:text-base">{lead.excerpt}</p>}
                  <Meta p={lead} className="mt-6" />
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-saffron transition-transform group-hover:translate-x-1"><T>Read the story</T> <ArrowRight className="h-4 w-4" /></span>
                </div>
              </Link>
            )}

            {/* Rest of the grid */}
            {rest.length > 0 && (
              <div className="grid gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
                {rest.map((p) => (
                  <Link key={p.slug} to={`/patrika/${p.slug}`} data-testid={`journal-card-${p.slug}`} className="group flex flex-col">
                    {p.cover_image ? (
                      <div className="mb-5 aspect-[16/10] overflow-hidden border border-line">
                        <img src={normalizeSrc(p.cover_image)} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </div>
                    ) : (
                      <div className="mb-5 flex aspect-[16/10] items-center justify-center border border-line bg-[var(--surface-alt)]">
                        <span className="font-deva text-4xl text-gold/40 deva-lh">{p.deva_title || "पत्रिका"}</span>
                      </div>
                    )}
                    {p.deva_title && <p className="font-deva text-base text-saffron deva-lh">{p.deva_title}</p>}
                    <h2 className="mt-1 font-serif-latin text-2xl leading-snug text-ink transition-colors group-hover:text-saffron">{p.title}</h2>
                    {p.excerpt && <p className="mt-2.5 flex-1 text-[15px] leading-relaxed text-ink-2 line-clamp-3">{p.excerpt}</p>}
                    <div className="mt-4 border-t border-line pt-3"><Meta p={p} /></div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}
